/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { ICreateClientOpts } from '../../@types/client';
import type { IEvent } from '../../@types/event';
import type { XHubChatClient } from '../../client';
import { XHubChatEventEvent } from '../../@types/event';
import { XHubChatEvent } from './event';

export type IXHubChatClientCreateOpts = {} & ICreateClientOpts;

export type EventMapper = (obj: Partial<IEvent>) => XHubChatEvent;
export type MapperOpts = {
  // don't re-emit events emitted on an event mapped by this mapper on the client
  preventReEmit?: boolean;
};

export function eventMapperFor(client: XHubChatClient, options: MapperOpts): EventMapper {
  let preventReEmit = Boolean(options.preventReEmit);
  function mapper(plainOldJsObject: Partial<IEvent>): XHubChatEvent {
    const room = client.getRoom(plainOldJsObject.room_id);

    let event: XHubChatEvent | undefined;
    // If the event is already known to the room, let's re-use the model rather than duplicating.
    // We avoid doing this to state events as they may be forward or backwards looking which tweaks behaviour.
    if (room && plainOldJsObject.state_key === undefined) {
      event = room.findEventById(plainOldJsObject.id!);
    }

    if (!event || event.status) {
      event = new XHubChatEvent(plainOldJsObject);
    } else {
      // merge the latest unsigned data from the server
      event.setUnsigned({ ...event.getUnsigned(), ...plainOldJsObject.unsigned });
      // prevent doubling up re-emitters
      preventReEmit = true;
      event.updatePinTime(plainOldJsObject.time_pin ?? null);
    }

    const thread = room?.findThreadForEvent(event);
    if (thread) {
      event.setThread(thread);
    }

    if (!preventReEmit) {
      room?.reEmitter.reEmit(event, [XHubChatEventEvent.BeforeRedaction]);
    }
    return event;
  }

  return mapper;
}
